import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';

interface QueueStats {
  name: string;
  active: number;
  waiting: number;
  delayed: number;
  failed: number;
  completed: number;
  paused: number;
  stalled: number;
  isPaused: boolean;
}

// Pipeline flow for visualization (actual queue order)
const PIPELINE_FLOW = [
  { key: 'extract', label: 'Extract' },
  { key: 'evolve', label: 'Evolve' },
  { key: 'deepen', label: 'Deepen' },
  { key: 'generate', label: 'Generate' },
  { key: 'send', label: 'Send' },
] as const;

// Queue cards display order
const QUEUE_DISPLAY = [
  { key: 'ingest', label: 'Ingest' },
  { key: 'extract', label: 'Extract' },
  { key: 'evolve', label: 'Evolve' },
  { key: 'deepen', label: 'Deepen' },
  { key: 'generate', label: 'Generate' },
  { key: 'send', label: 'Send' },
  { key: 'scheduler', label: 'Scheduler' },
] as const;

export function QueuesPage() {
  const [queues, setQueues] = useState<QueueStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQueues = async () => {
    try {
      const data = await adminApi.getOverview();
      setQueues(data.queues);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load queues');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQueues();
    // Refresh every 5 seconds
    const interval = setInterval(fetchQueues, 5000);
    return () => clearInterval(interval);
  }, []);

  const getTotalJobs = (queue: QueueStats) =>
    queue.active + queue.waiting + queue.delayed + queue.failed + queue.paused + queue.stalled;

  // Returns both text and background colors for tiles
  const getTileStyles = (
    count: number,
    type: 'failed' | 'active' | 'waiting' | 'delayed' | 'paused' | 'stalled',
  ) => {
    if (count === 0) return { text: 'text-mist/50', bg: 'bg-void' };
    switch (type) {
      case 'failed':
        return { text: 'text-white', bg: 'bg-ember/40' };
      case 'active':
        return { text: 'text-white', bg: 'bg-success/40' };
      case 'waiting':
        return { text: 'text-white', bg: 'bg-amber-500/40' };
      case 'delayed':
        return { text: 'text-white', bg: 'bg-cyan-500/40' };
      case 'paused':
        return { text: 'text-white', bg: 'bg-mist/30' };
      case 'stalled':
        return { text: 'text-white', bg: 'bg-rose-500/40' };
      default:
        return { text: 'text-mist', bg: 'bg-void' };
    }
  };

  // Get queue by name
  const getQueue = (name: string) => queues.find((q) => q.name === name);

  // Order queues for display
  const orderedQueues = QUEUE_DISPLAY.map((stage) => ({
    ...stage,
    queue: getQueue(stage.key),
  })).filter((item) => item.queue !== undefined);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl text-parchment">Queue Monitor</h1>
        <button
          onClick={() => {
            setIsLoading(true);
            fetchQueues();
          }}
          className="px-3 py-1.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50"
        >
          Refresh
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-ember/10 border border-ember/30 rounded-lg text-ember">{error}</div>
      )}

      {/* Loading State */}
      {isLoading && queues.length === 0 && (
        <div className="text-center py-12">
          <div className="text-mist animate-pulse">Loading queues...</div>
        </div>
      )}

      {/* Pipeline Flow Indicator */}
      {orderedQueues.length > 0 && (
        <div className="flex items-center justify-center gap-2 text-xs text-mist/60 overflow-x-auto py-2">
          {PIPELINE_FLOW.map((stage, idx) => {
            const queue = getQueue(stage.key);
            const isActive = queue && queue.active > 0;
            return (
              <div key={stage.key} className="flex items-center gap-2 whitespace-nowrap">
                <span
                  className={`px-2 py-1 rounded ${isActive ? 'bg-success/30 text-success' : 'bg-stone/20'}`}
                >
                  {stage.label}
                </span>
                {idx < PIPELINE_FLOW.length - 1 && <span className="text-stone">→</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Queue Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {orderedQueues.map(({ key, label, queue }) => (
          <div
            key={key}
            className={`bg-night border rounded-xl p-6 ${
              queue!.active > 0 ? 'border-success/50' : 'border-stone/30'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg text-parchment">{label}</h2>
              <div className="flex items-center gap-2">
                {queue!.isPaused && (
                  <span className="text-xs text-mist bg-stone/30 px-2 py-1 rounded">Paused</span>
                )}
                <span className="text-xs text-mist bg-stone/30 px-2 py-1 rounded">
                  {getTotalJobs(queue!)} pending
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {(['active', 'waiting', 'delayed', 'failed', 'paused', 'stalled'] as const).map(
                (status) => {
                  const count = queue![status];
                  const styles = getTileStyles(count, status);
                  return (
                    <div key={status} className={`text-center p-3 rounded-lg ${styles.bg}`}>
                      <div className={`text-2xl font-bold ${styles.text}`}>{count}</div>
                      <div className="text-xs text-mist mt-1 capitalize">{status}</div>
                    </div>
                  );
                },
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-stone/20">
              <div className="flex justify-between text-sm">
                <span className="text-mist">Completed</span>
                <span className="text-parchment">{queue!.completed.toLocaleString()}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {queues.length === 0 && !isLoading && !error && (
        <div className="text-center py-12 text-mist">No queues found</div>
      )}

      {/* Auto-refresh indicator */}
      <div className="text-center text-xs text-mist/50">Auto-refreshes every 5 seconds</div>
    </div>
  );
}
